import createClient from 'openapi-fetch';
import { watchTabAccount } from '../owner/tab';
import { watchSuspension } from '../suspension.svelte';
import { isTillCall, storeFetch } from '../till/shell';
import type { paths } from './types';
import type { paths as storePaths } from './store';

// The API is a different origin, api.<domain>, while this bundle is served from
// app.<domain>. Two consequences, both mandatory.
//
// credentials: 'include' or the session cookie is never sent, and every request
// arrives unauthenticated.
//
// The base URL comes from the build, never from a literal. A hard-coded host
// would tie one bundle to one deployment.
const baseUrl = import.meta.env.VITE_API_BASE;

if (!baseUrl) {
	throw new Error(
		'VITE_API_BASE is not set. The bundle and the API are separate origins, ' +
			'so the API origin must be supplied at build time.',
	);
}

// Sign-in is a redirect, not a fetch: the browser must leave this origin for
// Google's consent screen and come back. So the API origin is needed as a
// string as well as inside the client.
export const apiBase: string = baseUrl;

// The till never calls the API itself (#371): every /api/till/* call goes to
// its order store through the shell (src/till/shell.ts), which answers the
// till API's own paths. The owner dashboard calls the API as it always has.
function route(request: Request): Promise<Response> {
	return isTillCall(request.url) ? storeFetch(request) : fetch(request);
}

export const api = createClient<paths>({
	baseUrl,
	credentials: 'include',
	fetch: route,
});

// Every answer passes the suspension watch (UC-23), so no caller has to look
// for ACCOUNT_SUSPENDED itself.
api.use(watchSuspension);

// A dashboard tab names the restaurant it loaded on every /api/owner/ call,
// and reloads when the browser is now signed in to another (UC-64).
api.use(watchTabAccount);

// What only the order store knows, such as the till's closed orders the
// server has not confirmed yet (internal/orderstore/openapi.json). It is
// reached through the shell alone; the origin only builds the request.
export const store = createClient<storePaths>({ baseUrl, fetch: storeFetch });
